/**
 * Environment configuration with validation
 * Prevents common configuration errors in production
 */

interface EnvConfig {
  apiUrl: string
  isDevelopment: boolean
  isProduction: boolean
}

class EnvironmentConfig implements EnvConfig {
  public readonly apiUrl: string
  public readonly isDevelopment: boolean
  public readonly isProduction: boolean

  constructor() {
    // Use nullish coalescing (??) to preserve empty string for production
    this.apiUrl = import.meta.env.VITE_API_URL ?? ''
    this.isDevelopment = import.meta.env.DEV
    this.isProduction = import.meta.env.PROD

    // Validate configuration
    this.validate()
  }

  private validate(): void {
    // Check for localhost in production build
    if (this.isProduction && this.apiUrl.includes('localhost')) {
      console.error('❌ Configuration Error: localhost URL detected in production build!')
      console.error('   Current API URL:', this.apiUrl)
      console.error('   Expected: empty string (to use Nginx proxy)')

      throw new Error(
        'Invalid production configuration: localhost URL is not allowed. ' +
        'Please ensure VITE_API_URL is empty in production .env file.'
      )
    }

    // Development environment should have explicit API URL
    if (this.isDevelopment && !this.apiUrl) {
      console.warn('⚠️  Warning: VITE_API_URL is empty in development mode')
      console.warn('   Using Vite proxy configuration (see vite.config.ts)')
    }

    // Log configuration in development
    if (this.isDevelopment) {
      console.log('🔧 Environment Configuration:')
      console.log('   API URL:', this.apiUrl || '(using Vite proxy)')
      console.log('   Mode:', import.meta.env.MODE)
    }
  }

  /**
   * Get the base URL for API requests
   * In production: empty string (uses relative paths via Nginx proxy)
   * In development: explicit URL or Vite proxy
   */
  getApiBaseUrl(): string {
    return this.apiUrl
  }
}

// Create singleton instance and validate on app startup
const envConfig = new EnvironmentConfig()

export default envConfig
